import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { candidateAPI, courseAPI } from '../services/api';
import toast from 'react-hot-toast';
import { formatCourseDurationLabel } from '../constants/courseDurations';
import AiDisclaimer from './AiDisclaimer';

function normalizeSkillNames(skills) {
  if (!skills) return [];
  if (typeof skills === 'string') {
    return skills
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (!Array.isArray(skills)) return [];
  return skills
    .map((s) => (typeof s === 'string' ? s : s.name || s.skill_name))
    .filter(Boolean);
}

/** One row per course_id (first wins; avoids duplicate UI if API ever repeats). */
function dedupeEnrollmentsByCourse(enrollments) {
  const byCourse = new Map();
  for (const e of enrollments || []) {
    if (!e?.course_id) continue;
    if (!byCourse.has(e.course_id)) byCourse.set(e.course_id, e);
  }
  return Array.from(byCourse.values()).sort((a, b) => {
    const ta = new Date(a.requested_at || 0).getTime();
    const tb = new Date(b.requested_at || 0).getTime();
    return tb - ta;
  });
}

function dedupeMatchesById(matches) {
  const seen = new Set();
  const out = [];
  for (const c of matches || []) {
    if (!c?.id || seen.has(c.id)) continue;
    seen.add(c.id);
    out.push(c);
  }
  return out;
}

function enrollmentCanOpenContent(status) {
  const s = String(status || '').toLowerCase();
  return ['approved', 'active', 'completed'].includes(s);
}

function enrollmentCourseButtonLabel(status) {
  const s = String(status || '').toLowerCase();
  if (['approved', 'active', 'completed'].includes(s)) return 'Open course';
  if (s === 'requested') return 'Awaiting approval';
  if (s === 'rejected') return 'Access declined';
  return 'Unavailable';
}

/** Candidate-facing label for where they are in the course lifecycle. */
function enrollmentLearningBadge(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'completed') {
    return {
      label: 'Completed',
      className: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-600/20',
    };
  }
  if (s === 'approved' || s === 'active') {
    return {
      label: 'In progress',
      className: 'bg-amber-100 text-amber-900 ring-1 ring-amber-600/25',
    };
  }
  if (s === 'requested') {
    return {
      label: 'Awaiting approval',
      className: 'bg-slate-100 text-slate-700 ring-1 ring-slate-300',
    };
  }
  if (s === 'rejected') {
    return {
      label: 'Declined',
      className: 'bg-red-100 text-red-800 ring-1 ring-red-300',
    };
  }
  return {
    label: s ? s.replace(/_/g, ' ') : 'Unknown',
    className: 'bg-gray-100 text-gray-700',
  };
}

const CandidateCourses = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [skillNames, setSkillNames] = useState([]);
  const [matches, setMatches] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [coursesTab, setCoursesTab] = useState('available');
  const [requestingCourseId, setRequestingCourseId] = useState(null);

  const displayEnrollments = useMemo(() => dedupeEnrollmentsByCourse(enrollments), [enrollments]);

  const enrolledCourseIds = useMemo(
    () => new Set(displayEnrollments.map((e) => e.course_id).filter(Boolean)),
    [displayEnrollments]
  );

  const availableMatches = useMemo(() => {
    const unique = dedupeMatchesById(matches);
    return unique.filter((c) => c.id && !enrolledCourseIds.has(c.id));
  }, [matches, enrolledCourseIds]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const profile = await candidateAPI.getProfile();
      const names = normalizeSkillNames(profile.data?.skills);
      setSkillNames(names);

      if (names.length === 0) {
        setMatches([]);
      } else {
        const m = await courseAPI.matchingCourses(names);
        setMatches(m.data || []);
      }

      const e = await courseAPI.myEnrollments();
      setEnrollments(e.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const requestAccess = async (courseId) => {
    if (!courseId || requestingCourseId === courseId) return;
    if (enrolledCourseIds.has(courseId)) {
      toast.error('You already requested or enrolled in this course.');
      setCoursesTab('enrolled');
      return;
    }
    try {
      setRequestingCourseId(courseId);
      const res = await courseAPI.requestCourseAccess(courseId);
      toast.success(res.data?.message || 'Request sent');
      setCoursesTab('enrolled');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    } finally {
      setRequestingCourseId(null);
    }
  };

  const openCourse = (courseId, status) => {
    const allowed = new Set(['approved', 'active', 'completed']);
    if (!allowed.has(String(status).toLowerCase())) {
      toast.error('Wait for admin approval to open the course');
      return;
    }
    navigate(`/dashboard/candidate/course/${courseId}`);
  };

  const tabNav = (
    <nav className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
      {[
        { id: 'available', label: 'Available for you', count: availableMatches.length },
        { id: 'enrolled', label: 'Enrolled', count: displayEnrollments.length },
      ].map(({ id, label, count }) => (
        <button
          key={id}
          type="button"
          onClick={() => setCoursesTab(id)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium ${
            coursesTab === id ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {label}
          <span className={`ml-1.5 tabular-nums ${coursesTab === id ? 'text-primary-100' : 'text-gray-500'}`}>
            ({count})
          </span>
        </button>
      ))}
    </nav>
  );

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Learn What You Are Missing</h2>
        <p className="mt-1 text-sm text-gray-600">
          Get learning recommendations based on the skills you need to improve. Request access once per course; an admin approves before content unlocks.
        </p>
        <div className="mt-3">
          <AiDisclaimer variant="general" />
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-600">Loading…</p>
      ) : (
        <>
          {skillNames.length === 0 ? (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
              Add skills to your profile to see courses matched to you in <strong>Available for you</strong>. You can
              still open <strong>Enrolled</strong> if you already have requests or access.
            </div>
          ) : (
            <p className="text-xs text-gray-500">Matching on skills: {skillNames.join(', ')}</p>
          )}

          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm space-y-4">
            {tabNav}

            {coursesTab === 'available' && (
              <ul className="space-y-3">
                {availableMatches.map((c) => (
                  <li key={c.id} className="rounded-lg border border-gray-100 p-3">
                    <div className="font-medium text-gray-900">{c.title}</div>
                    <p className="mt-1 text-xs text-gray-500">
                      {c.skill_name} · {formatCourseDurationLabel(c.duration_days)}
                    </p>
                    <p className="mt-2 line-clamp-3 text-sm text-gray-600">{c.summary}</p>
                    <button
                      type="button"
                      disabled={requestingCourseId === c.id}
                      onClick={() => requestAccess(c.id)}
                      className="mt-2 text-sm text-primary-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {requestingCourseId === c.id ? 'Sending request…' : 'Request access'}
                    </button>
                  </li>
                ))}
                {!loading && availableMatches.length === 0 && (
                  <li className="text-sm text-gray-500">
                    {skillNames.length === 0
                      ? 'Add profile skills to discover courses here.'
                      : enrolledCourseIds.size > 0 && matches.length > 0
                        ? 'All matching courses already have a request or enrollment — see the Enrolled tab.'
                        : 'No published courses for your skills yet.'}
                  </li>
                )}
              </ul>
            )}

            {coursesTab === 'enrolled' && (
              <ul className="space-y-2">
                {displayEnrollments.map((e) => {
                  const canOpen = enrollmentCanOpenContent(e.status);
                  const badge = enrollmentLearningBadge(e.status);
                  return (
                    <li
                      key={e.id}
                      className="flex flex-col gap-3 border-b border-gray-100 py-4 text-sm sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <div className="font-medium text-gray-800">{e.course_title}</div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={!canOpen}
                        onClick={() => openCourse(e.course_id, e.status)}
                        title={
                          canOpen
                            ? 'Open course content'
                            : String(e.status || '').toLowerCase() === 'requested'
                              ? 'An admin must approve your request before you can open this course'
                              : String(e.status || '').toLowerCase() === 'rejected'
                                ? 'Your access request was not approved'
                                : 'Course is not available to open yet'
                        }
                        className={`inline-flex w-full items-center justify-center rounded-lg px-5 py-3 text-sm font-semibold shadow-sm sm:w-auto sm:min-w-[8.5rem] ${
                          canOpen
                            ? 'bg-primary-600 text-white hover:bg-primary-700'
                            : 'cursor-not-allowed bg-gray-200 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {enrollmentCourseButtonLabel(e.status)}
                      </button>
                    </li>
                  );
                })}
                {displayEnrollments.length === 0 && (
                  <li className="text-sm text-gray-500">No enrollments or access requests yet.</li>
                )}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CandidateCourses;
